import type { ErrorView, QueryView } from '@krp/client-runtime'

import type { CommandStatus } from '../../../shared/model/commandOutcome'
import type { CommandView } from '../../../shared/model/commandView'

/**
 * Модель фичи «Товары 1С» (RP-UB1a): поиск, карточка, правка, пометка
 * удаления, штрихкоды, создание и дубли. Всё — внутри конкретной базы:
 * rid входит в аргументы запросов, ключи черновиков и операций и в адрес окна.
 */

// --- данные службы (живут в кэше RTK Query, не в слайсе) ---------------------

export interface ProductCandidate {
  /** Ссылка 1С товара В ЭТОЙ базе. */
  refKey: string
  name: string
  sku: string | null
  barcode: string | null
  /** Чем найден (`Barcode`, `Sku`, `ExactName`, …). */
  strategy: string | null
}

export interface ProductCard {
  refKey: string
  name: string
  article: string | null
  primaryBarcode: string | null
  /** Ставка НДС кодом чека (`none`, `vat20`, `vat22`…); `null` — не указана, не сопоставилась или база её не публикует (см. `vatState`). */
  vatRateCode?: string | null
  /** `set`, `missing`, `unmapped`, `unavailable`. */
  vatState?: string
}

export interface ProductBarcode {
  barcode: string
  isMain: boolean
}

export interface CreateProductResult {
  refKey: string
  outcome: 'Created' | 'AlreadyExists'
  barcodeWarning: string | null
}

export interface BarcodeMutationResult {
  outcome: string
  message: string | null
}

// --- намерение человека (слайсы) --------------------------------------------

export type SearchMode = 'text' | 'barcode'

/** Что человек ищет в окне товаров одной базы. Не копия результатов. */
export interface ProductBrowseIntent {
  text: string
  /** Отправленный запрос: поиск идёт по нему, а не по каждой нажатой клавише. */
  submitted: { mode: SearchMode; value: string } | null
}

export interface ProductFields {
  name: string
  article: string
  barcode: string
}

export type ProductField = keyof ProductFields

/** Цель черновика/операции: ссылка товара или `new` для окна создания. */
export const NEW_PRODUCT = 'new'
/** Цель операции «Обновить каталог из 1С»: не товар, а зеркало каталога базы. */
export const CATALOG_TARGET = 'catalog'
/** Вид обновления каталога: товары (`RefreshKind.Products` службы). */
export const CATALOG_REFRESH_KIND = 'Products'

/** Итог обновления каталога (`TriggerSyncResultDto`): счётчики зеркала службы, не создание товаров в 1С. */
export interface CatalogRefreshResult {
  refreshKind: string
  read: number
  created: number
  updated: number
  skipped: number
}

/**
 * Черновик правки — ДЕЛЬТА поверх значений, с которых человек начал (`base`).
 * Сравнение `base` со свежей карточкой и есть проверка оптимистичного
 * конфликта: кто-то поменял поле в 1С, пока человек правил.
 */
export interface ProductDraft {
  resourceId: string
  target: string
  base: ProductFields
  values: Partial<ProductFields>
  /** Ревизия конфигурации базы, при которой начата правка. */
  baseVersion: string | null
  /** Окно создания: по какому наименованию показаны похожие товары. */
  checkedName: string | null
  startedAt: string
}

export interface ProductsIntentState {
  /** Ключ — rid. */
  browse: Readonly<Record<string, ProductBrowseIntent>>
  /** Ключ — `<rid>|<target>`. */
  drafts: Readonly<Record<string, ProductDraft>>
}

export type ProductAction = 'create' | 'update' | 'delete' | 'barcode-add' | 'barcode-delete' | 'barcode-main' | 'catalog-refresh'

export type ProductOperationPayload =
  | { kind: 'create'; name: string; article: string | null; barcode: string | null }
  | { kind: 'update'; name?: string; article?: string }
  | { kind: 'delete' }
  | { kind: 'barcode'; barcode: string }
  | { kind: 'catalog-refresh'; refreshKind: string }

export type ProductOperationResult =
  | { kind: 'created'; refKey: string; outcome: 'Created' | 'AlreadyExists'; barcodeWarning: string | null }
  | { kind: 'updated'; changed: boolean }
  | { kind: 'deleted'; deleted: boolean }
  | { kind: 'barcode'; outcome: string; message: string | null }
  | ({ kind: 'refreshed' } & CatalogRefreshResult)

/**
 * Пишущая операция: ИДЕНТИЧНОСТЬ команды (operationId, ключ, ревизия, данные)
 * создаётся один раз на намерение и хранится, чтобы «Проверить исход»
 * отправил ту же команду, а не новую. Ответ службы здесь — только итог
 * команды, данные товара остаются в кэше.
 */
export interface ProductOperation {
  key: string
  resourceId: string
  target: string
  action: ProductAction
  operationId: string
  idempotencyKey: string
  expectedConfigurationVersion: string
  payload: ProductOperationPayload
  status: CommandStatus
  resendable: boolean
  error: ErrorView | null
  result: ProductOperationResult | null
  startedAt: string
}

/** Ключ — `<rid>|<target>|<action>[|<штрихкод>]`. */
export type ProductOperationsState = Readonly<Record<string, ProductOperation>>

// --- vm и actions экранов ----------------------------------------------------

export interface ProductRowVm {
  refKey: string
  name: string
  sku: string | null
  barcode: string | null
  /** Есть ли фото в кэше службы; `null` — не знаем. */
  hasPhoto: boolean | null
  /** Адрес главного фото, когда оно есть: нажатие на миниатюру открывает его на весь экран (RP-FB14). */
  photo?: string | null
}

export interface ProductsVm {
  resourceId: string
  text: string
  submitted: ProductBrowseIntent['submitted']
  query: QueryView
  rows: readonly ProductRowVm[]
  /** Сканер камеры в этом браузере есть. */
  cameraScanAvailable: boolean
  /** «Обновить каталог из 1С»: итог/отказ команды; `null` — не запускали. */
  catalogRefresh: (CommandView & { key: string }) | null
}

export interface ProductsActions {
  textChanged: (text: string) => void
  searchSubmitted: () => void
  barcodeSubmitted: (barcode: string) => void
  cleared: () => void
  reload: () => void
  /** Обновить зеркало каталога этой базы из 1С (`Catalog_TriggerSync`), затем перечитать поиск. */
  refreshCatalog: () => void
  /** «Проверить исход» и «Скрыть» итога обновления каталога. */
  resendOperation: (key: string) => void
  dismissOperation: (key: string) => void
  open: (refKey: string) => void
  create: () => void
}

export interface FieldVm {
  value: string
  /** Значение в 1С сейчас; `null` — карточка не загружена. */
  server: string | null
  changed: boolean
  /** Поле изменили и в 1С, и здесь — нужно решение человека. */
  conflict: boolean
}

export interface ConflictVm {
  field: ProductField
  label: string
  server: string
  mine: string
  /** С чего человек начинал. */
  base: string
}

export interface BarcodeRowVm {
  barcode: string
  isMain: boolean
  busy: boolean
}

export interface ProductCardVm {
  resourceId: string
  refKey: string
  query: QueryView
  card: ProductCard | null
  form: { name: FieldVm; article: FieldVm }
  dirty: boolean
  draftStartedAt: string | null
  conflicts: readonly ConflictVm[]
  /** Пока исход команды неизвестен или она в пути, правка закрыта. */
  locked: boolean
  canSave: boolean
  /** Нельзя сохранить — почему (словами). */
  saveHint: string | null
  commands: readonly (CommandView & { key: string })[]
  barcodesQuery: QueryView
  barcodes: readonly BarcodeRowVm[]
  /** Ревизия базы из последнего чтения — нужна командам штрихкодов и пометки. */
  configurationVersion: string | null
  markedForDeletion: boolean
}

export interface ProductCardActions {
  fieldChanged: (field: 'name' | 'article', value: string) => void
  save: () => void
  discard: () => void
  takeServer: (field: ProductField) => void
  keepMine: () => void
  resend: (operationKey: string) => void
  dismiss: (operationKey: string) => void
  reload: () => void
  markForDeletion: () => void
  addBarcode: (barcode: string) => void
  deleteBarcode: (barcode: string) => void
  setMainBarcode: (barcode: string) => void
  /** Закрыть окно карточки. */
  close: () => void
  /**
   * Поставить (или снять, `null`) сторожа закрытия окна: пока в карточке есть несохранённая правка, крестик окна, Esc
   * и «Закрыть» сперва спрашивают подтверждение. Сторож возвращает `true`, если окно можно закрыть сразу.
   */
  closeGuard: (guard: (() => boolean) | null) => void
}

export interface ProductCreateVm {
  resourceId: string
  form: { name: FieldVm; article: FieldVm; barcode: FieldVm }
  dirty: boolean
  /** Похожие товары этой базы по наименованию — до создания. */
  similarQuery: QueryView
  similar: readonly ProductRowVm[]
  checkedName: string | null
  locked: boolean
  canCreate: boolean
  createHint: string | null
  command: (CommandView & { key: string }) | null
  created: CreateProductResult | null
}

export interface ProductCreateActions {
  fieldChanged: (field: ProductField, value: string) => void
  checkSimilar: () => void
  create: () => void
  discard: () => void
  resend: (operationKey: string) => void
  dismiss: (operationKey: string) => void
  open: (refKey: string) => void
  close: () => void
}

